import React, { useState, useRef, useEffect } from 'react'
import { flushSync } from 'react-dom'
import Modal, { ModalProps } from 'components/modal'
import Button from 'components/button'
import { useApplicationStore } from 'hooks/application'
import Checkbox from 'components/checkbox'
import { ITableData, Table } from 'components/table'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import ErrorBoundary from 'components/error'
import useProject from 'hooks/useProject'
import { transformStringToCorrectFormat } from 'components/spreadsheet/helpers/paste'
import {
  DATE_FORMAT_CANDIDATES,
  DATETIME_FORMAT_CANDIDATES,
  detectDatetimeFormats
} from 'components/spreadsheet/helpers/date'
import { formatDate, formatDateTime } from 'components/spreadsheet/helpers/format'
import { convertNameMapToRowDataArray, isColumnLockedByRules } from 'components/spreadsheet/helpers/functions'
import { AllowedDateFormats, AllowedDatetimeFormats, ITableRow, ICellValue } from 'types'
import ProgressBar from 'components/progressBar'
import {
  EXTENSIONS_XLS,
  readExcelAsCsv,
  convertExcelTimestampToISODate,
  convertExcelTimestampToISODatetime,
  isExcelTimestamp,
  parseCsvColumns,
  parseCsvRows,
  readCsvPreview,
  streamCsvChunks
} from './helpers'
import Select from 'components/select'
import api from 'helpers/api'
import ImportFromTableSelection from '../importFromTable'
import Skeleton from 'components/skeleton'

const LARGE_FILE_WARNING_SIZE = 1000000 // 1 MB

interface AppendFileModalProps extends ModalProps {
  fromTable?: boolean
}

const AppendFileModal: React.FC<AppendFileModalProps> = ({ id, open, setOpen, fromTable }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const inputRef = useRef<HTMLInputElement>(null)

  const [rawCsvData, setRawCsvData] = useState<string>()
  const [insertMethod, setInsertMethod] = useState<string>('upsert')
  const [mergingColumn, setMergingColumn] = useState<string>()
  const [allParsedRows, setAllParsedRows] = useState<Array<Record<string, string>>>()
  const [excludeFirstRow, setExcludeFirstRow] = useState<boolean>(true)
  const [excludeBlankRows, setExcludeBlankRows] = useState<boolean>(true)
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [fileImportLoading, setFileImportLoading] = useState<boolean>(false)
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null)
  const [showAdditionalOptions, setShowAdditionalOptions] = useState<boolean>(false)
  const [isExcelFile, setIsExcelFile] = useState<boolean>(false)
  const [fileTruncated, setFileTruncated] = useState(false)
  // Defaults will be updated when file is loaded
  const [dateFormat, setDateFormat] = useState<AllowedDateFormats>('DD/MM/YYYY')
  const [datetimeFormat, setDatetimeFormat] = useState<AllowedDatetimeFormats>('DD/MM/YYYY HH:mm:ss')
  const [showAllRows, setShowAllRows] = useState<boolean>(false)
  const [cancelling, setCancelling] = useState(false)
  const cancelledRef = useRef(false)
  const importedRowIdsRef = useRef<string[]>([]) // All rows created on server (for server-side deletion)
  const dispatchedRowIdsRef = useRef<string[]>([]) // Only rows dispatched to local state via APPEND_ROWS
  const importFinishedResolveRef = useRef<(() => void) | null>(null)

  // Clean up all state when modal closes
  useEffect(() => {
    if (!open) {
      setRawCsvData(undefined)
      setAllParsedRows(undefined)
      setFile(null)
      setShowAllRows(false)
      setFileTruncated(false)
      cancelledRef.current = false
      setCancelling(false)
      importedRowIdsRef.current = []
      dispatchedRowIdsRef.current = []
      importFinishedResolveRef.current = null
    }
  }, [open])

  // Stable header IDs for parsing - doesn't change when insertMethod/mergingColumn changes
  const computeHeaderIds = () => {
    const hiddenSet = new Set(spreadsheetData.userConfiguration.hiddenColumns)
    return spreadsheetData.viewDetails.columns
      .filter((column) => !hiddenSet.has(column.publicId))
      .map((column) => column.publicId)
  }
  const headerIds = computeHeaderIds()

  // Headers with display labels (includes lock icons) - used for table display only
  const computeHeaders = () => {
    const hiddenSet = new Set(spreadsheetData.userConfiguration.hiddenColumns)
    return spreadsheetData.viewDetails.columns
      .filter((column) => !hiddenSet.has(column.publicId))
      .map((column) => ({
        id: column.publicId,
        header:
          !column.isJoined &&
          !isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) &&
          ((insertMethod === 'upsert' &&
            (column.scriptEnabled || column.formulaEnabled) &&
            column.name === mergingColumn) ||
            (!column.scriptEnabled && !column.formulaEnabled))
            ? column.name
            : `${column.name} 🔒`
      }))
  }
  const headers = computeHeaders()

  const noHeadersError = headers.length === 0

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || noHeadersError) return

    setFileImportLoading(true)
    setShowAllRows(false)

    const file = e.target.files[e.target.files.length - 1]
    setFile(file)

    const extension = (file.name.split('.').pop() || '').toLowerCase()
    const isExcel = EXTENSIONS_XLS.includes(extension)
    setIsExcelFile(isExcel)

    try {
      if (isExcel) {
        const csvData = await readExcelAsCsv(file)
        setRawCsvData(csvData)
        setFileTruncated(false)
      } else {
        // Only read a preview (first ~2 MB) to avoid loading huge files into memory
        const { text, truncated } = await readCsvPreview(file)
        setRawCsvData(text)
        setFileTruncated(truncated)
      }
    } catch {
      setSnackbarMessage({
        status: 'error',
        message: "Morta couldn't read this file. Check it's a CSV or Excel file and try again."
      })
      setFileImportLoading(false)
    }
  }

  const handleTableCsvReady = (csvData: string) => {
    setIsExcelFile(false) // CSV from table view will never be Excel
    setRawCsvData(csvData)

    // Dummy file
    setFile(new File([], 'morta-table-import.csv'))
  }

  // Parse CSV once when rawCsvData changes - excludeFirstRow/excludeBlankRows filtering is done separately
  useEffect(() => {
    if (rawCsvData && spreadsheetData.viewDetails.columns) {
      const parseCsvData = (): { data: Array<Record<string, string>>; errorMessage: string } => {
        let errorMessage = ''

        errorMessage =
          'Unable to split the data into rows. Please ensure each line is separated by a new‑line character (\\r\\n).'
        const csvRows = parseCsvRows(rawCsvData)

        errorMessage = 'Unable to split the data into columns. Please ensure columns are delimited by a comma (,).'

        const data: Array<Record<string, string>> = []
        // Parse ALL rows (filtering happens below)
        for (let i = 0; i < csvRows.length; i++) {
          const rowData = parseCsvColumns(csvRows[i])
          const dataObject: Record<string, string> = {}
          for (let j = 0; j < headerIds.length; j++) {
            const hasValue = rowData && rowData[j] && rowData[j] !== 'null'
            let value = hasValue ? rowData[j] : ''
            // Strip outer CSV quotes first, then unescape doubled quotes.
            // Order matters: stripping first ensures the outer delimiters are
            // removed before '""' → '"' conversion touches them.
            if (value.charAt(0) === '"' && value.charAt(value.length - 1) === '"') {
              value = value.substring(1, value.length - 1)
            }
            value = value.replaceAll('""', '"')
            dataObject[headerIds[j]] = value
          }
          // Mark blank rows for filtering later
          const isBlank = !rowData || rowData.length === 0 || (rowData.length === 1 && rowData[0] === '')
          dataObject.__isBlankRow = isBlank ? 'true' : 'false'
          data.push(dataObject)
        }
        return { data, errorMessage }
      }

      let data: Array<Record<string, string>> = []
      try {
        const result = parseCsvData()
        data = result.data
        setAllParsedRows(data)
      } catch {
        setSnackbarMessage({
          status: 'error',
          message:
            "Morta couldn't read the rows in this CSV. Check that columns are separated by commas and each row is on its own line."
        })
      }

      // --- Format detection: only for CSV files, not Excel ---
      if (!isExcelFile && data.length > 0) {
        const { detectedDateFormat, detectedDatetimeFormat } = detectDatetimeFormats(
          spreadsheetData.viewDetails.columns,
          data.slice(1) // Skip first row for detection (it's usually headers)
        )
        if (detectedDateFormat) setDateFormat(detectedDateFormat)
        if (detectedDatetimeFormat) setDatetimeFormat(detectedDatetimeFormat)
      }

      setFileImportLoading(false)
    }
  }, [rawCsvData, spreadsheetData.viewDetails.columns, setSnackbarMessage, isExcelFile, headerIds])

  // Filter rows based on excludeFirstRow/excludeBlankRows - this is fast since data is already parsed
  const computeParsedCsvData = () => {
    if (!allParsedRows) return undefined

    let filtered = allParsedRows
    if (excludeFirstRow) {
      filtered = filtered.slice(1)
    }
    if (excludeBlankRows) {
      filtered = filtered.filter((row) => row.__isBlankRow !== 'true')
    }
    return filtered
  }
  const parsedCsvData = computeParsedCsvData()

  const computeRowCountLabel = () => {
    if (!parsedCsvData) return ''
    if (fileTruncated && rawCsvData && allParsedRows && file) {
      const estimated = Math.round(allParsedRows.length * (file.size / rawCsvData.length))
      return ` (~${estimated.toLocaleString()} rows)`
    }
    return ` (${parsedCsvData.length.toLocaleString()} rows)`
  }
  const rowCountLabel = computeRowCountLabel()

  const handleImportCancel = async () => {
    cancelledRef.current = true
    setCancelling(true)

    // Wait for the in-flight batch to finish so all created rows are tracked
    if (importFinishedResolveRef.current === null) {
      await new Promise<void>((resolve) => {
        importFinishedResolveRef.current = resolve
      })
    }

    // Delete all created rows from the server (includes the last in-flight batch)
    const serverRowIds = importedRowIdsRef.current
    if (serverRowIds.length > 0) {
      try {
        await api.deleteTableRows(
          spreadsheetData.viewDetails.publicId,
          serverRowIds,
          project.publicId,
          spreadsheetData.processId,
          spreadsheetData.processSectionId
        )
      } catch {
        // If server cleanup fails, reload to get a consistent state
        flushSync(() => setLoading(false))
        window.location.reload()
        return
      }
    }

    // Remove only the rows that were dispatched to local state via APPEND_ROWS
    const localRowIds = dispatchedRowIdsRef.current
    if (localRowIds.length > 0) {
      setSpreadsheetData({ type: 'DELETE_ROWS', rowIds: localRowIds })
    }

    importedRowIdsRef.current = []
    dispatchedRowIdsRef.current = []
    setCancelling(false)
    setLoading(false)
    setImportProgress(null)
    setSnackbarMessage({ status: 'success', message: 'Import cancelled. All imported rows have been removed.' })
  }

  // ---------------------- Streaming import for large files ----------------------
  const handleStreamingImport = async (processContext?: { processId: string; processSectionId: string }) => {
    if (!file) return

    const ROW_LIMIT = 2500
    let batch: Record<string, string>[] = []
    let isFirstRow = true
    let batchCount = 0

    // Estimate total batches for progress display
    const previewTextLength = rawCsvData?.length || 1
    const previewRowCount = allParsedRows?.length || 1
    const estimatedTotalRows = Math.round(previewRowCount * (file.size / previewTextLength))
    const estimatedBatches = Math.max(1, Math.ceil(estimatedTotalRows / ROW_LIMIT))

    cancelledRef.current = false
    if (insertMethod === 'append') {
      importedRowIdsRef.current = []
      dispatchedRowIdsRef.current = []
    }
    setImportProgress({ current: 0, total: estimatedBatches })

    const sendBatch = async (batchRows: Record<string, string>[]) => {
      const rows = batchRows.map((data) => ({ rowData: cleanRow(data) }))

      if (insertMethod === 'append') {
        const response = await api.createNewRows(
          project.publicId,
          spreadsheetData.viewDetails.publicId,
          rows,
          processContext
        )

        const batchRowIds = response.data.map((r: any) => r.publicId)
        importedRowIdsRef.current.push(...batchRowIds)

        if (cancelledRef.current) {
          if (importFinishedResolveRef.current) importFinishedResolveRef.current()
          return false
        }

        const appendRows: ITableRow[] = response.data.map((responseRow: any) => {
          const rowDataArray = convertNameMapToRowDataArray(
            spreadsheetData.viewDetails.columns,
            responseRow.rowData as Record<string, ICellValue>,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.indexToColumnId
          )
          if (spreadsheetData.columnStore) {
            const rowIndex = spreadsheetData.columnStore.addRow()
            for (let k = 0; k < rowDataArray.length; k++) {
              spreadsheetData.columnStore.set(rowIndex, k, rowDataArray[k])
            }
            return { ...responseRow, _rowIndex: rowIndex, rowData: undefined } as ITableRow
          }
          return { ...responseRow, rowData: rowDataArray, _rowIndex: -1 } as ITableRow
        })

        dispatchedRowIdsRef.current.push(...batchRowIds)
        setSpreadsheetData({ type: 'APPEND_ROWS', rows: appendRows })
      } else {
        await api.upsertRows(spreadsheetData.viewDetails.publicId, rows, mergingColumn!)

        if (cancelledRef.current) {
          if (importFinishedResolveRef.current) importFinishedResolveRef.current()
          return false
        }
      }

      return !cancelledRef.current
    }

    const csvChunkIterator = streamCsvChunks(file)[Symbol.asyncIterator]()
    let csvChunkResult = await csvChunkIterator.next()
    while (!csvChunkResult.done) {
      if (cancelledRef.current) break
      const chunk = csvChunkResult.value

      const csvRows = parseCsvRows(chunk)

      for (const csvRow of csvRows) {
        if (cancelledRef.current) break

        if (isFirstRow && excludeFirstRow) {
          isFirstRow = false
          continue
        }
        isFirstRow = false

        const rowData = parseCsvColumns(csvRow)
        const isBlank = !rowData || rowData.length === 0 || (rowData.length === 1 && rowData[0] === '')
        if (excludeBlankRows && isBlank) continue

        const dataObject: Record<string, string> = {}
        for (let j = 0; j < headerIds.length; j++) {
          let value = rowData[j] && rowData[j] !== 'null' ? rowData[j] : ''
          if (value.charAt(0) === '"' && value.charAt(value.length - 1) === '"') {
            value = value.substring(1, value.length - 1)
          }
          value = value.replaceAll('""', '"')
          dataObject[headerIds[j]] = value
        }

        batch.push(dataObject)

        if (batch.length >= ROW_LIMIT) {
          const shouldContinue = await sendBatch(batch)
          batch = []
          batchCount++
          setImportProgress({ current: Math.min(batchCount, estimatedBatches), total: estimatedBatches })
          if (!shouldContinue) return
        }
      }
      csvChunkResult = await csvChunkIterator.next()
    }

    // Send remaining rows
    if (batch.length > 0 && !cancelledRef.current) {
      await sendBatch(batch)
      batchCount++
      setImportProgress({ current: Math.min(batchCount, estimatedBatches), total: estimatedBatches })
    }

    if (cancelledRef.current) {
      if (importFinishedResolveRef.current) importFinishedResolveRef.current()
      if (insertMethod === 'upsert') {
        flushSync(() => setLoading(false))
        window.location.reload()
      }
      return
    }

    if (insertMethod === 'append') {
      setLoading(false)
      setImportProgress(null)
      setSnackbarMessage({ status: 'success' })
      setOpen(false)
      setFileImportLoading(false)
    } else {
      flushSync(() => setLoading(false))
      window.location.reload()
    }
  }

  // ---------------------- Import logic ----------------------
  const performImport = async (processContext: { processId: string; processSectionId: string } | undefined) => {
    if (!parsedCsvData) return

    // Large files: stream from disk instead of using pre-parsed data
    if (fileTruncated && file) {
      await handleStreamingImport(processContext)
      return
    }

    const ROW_LIMIT = 2500
    const totalBatches = Math.ceil(parsedCsvData.length / ROW_LIMIT)

    if (insertMethod === 'append') {
      let batchCount = 0
      cancelledRef.current = false
      importedRowIdsRef.current = []
      dispatchedRowIdsRef.current = []
      setImportProgress({ current: 0, total: totalBatches })

      // Process and send rows in batches to avoid memory issues
      for (let i = 0; i < parsedCsvData.length; i += ROW_LIMIT) {
        if (cancelledRef.current) break

        // Clean rows lazily per batch instead of all upfront
        const batchData = parsedCsvData.slice(i, i + ROW_LIMIT)
        const rows = batchData.map((data) => ({ rowData: cleanRow(data) }))

        const response = await api.createNewRows(
          project.publicId,
          spreadsheetData.viewDetails.publicId,
          rows,
          processContext
        )

        // Always track server-side row IDs (even if cancelled mid-flight)
        const batchRowIds = response.data.map((responseRow: any) => responseRow.publicId)
        importedRowIdsRef.current.push(...batchRowIds)

        if (cancelledRef.current) {
          // Signal that the in-flight batch is done so handleImportCancel can clean up
          if (importFinishedResolveRef.current) importFinishedResolveRef.current()
          return
        }

        // Convert and dispatch rows immediately instead of accumulating
        // API returns rowData as object, need to convert to array format
        const batchRows: ITableRow[] = response.data.map((responseRow: any) => {
          const rowDataArray = convertNameMapToRowDataArray(
            spreadsheetData.viewDetails.columns,
            responseRow.rowData as Record<string, ICellValue>,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.indexToColumnId
          )

          if (spreadsheetData.columnStore) {
            const rowIndex = spreadsheetData.columnStore.addRow()
            for (let i = 0; i < rowDataArray.length; i++) {
              spreadsheetData.columnStore.set(rowIndex, i, rowDataArray[i])
            }
            return { ...responseRow, _rowIndex: rowIndex, rowData: undefined } as ITableRow
          }
          return { ...responseRow, rowData: rowDataArray, _rowIndex: -1 } as ITableRow
        })

        // Track rows dispatched to local state (separate from server-side tracking)
        dispatchedRowIdsRef.current.push(...batchRowIds)
        setSpreadsheetData({ type: 'APPEND_ROWS', rows: batchRows })

        batchCount++
        setImportProgress({ current: batchCount, total: totalBatches })
      }

      // If cancelled before any in-flight batch (e.g. between batches), signal done
      if (cancelledRef.current) {
        if (importFinishedResolveRef.current) importFinishedResolveRef.current()
        return
      }

      setLoading(false)
      setImportProgress(null)
      setSnackbarMessage({ status: 'success' })
      setOpen(false)
      setFileImportLoading(false)
    } else if (insertMethod === 'upsert' && mergingColumn) {
      let batchCount = 0
      cancelledRef.current = false
      setImportProgress({ current: 0, total: totalBatches })

      // Process and send rows in batches to avoid memory issues
      for (let i = 0; i < parsedCsvData.length; i += ROW_LIMIT) {
        if (cancelledRef.current) break

        const batchData = parsedCsvData.slice(i, i + ROW_LIMIT)
        const rows = batchData.map((data) => ({ rowData: cleanRow(data) }))
        await api.upsertRows(spreadsheetData.viewDetails.publicId, rows, mergingColumn)
        batchCount++
        setImportProgress({ current: batchCount, total: totalBatches })
      }

      // Flush loading=false synchronously so Modal's shouldBlockNavigationRef is updated
      // before reload triggers the beforeunload event
      flushSync(() => setLoading(false))

      if (cancelledRef.current) {
        if (importFinishedResolveRef.current) importFinishedResolveRef.current()
        // For upsert, reload to get a clean state since we can't easily undo merges
        window.location.reload()
        return
      }
      window.location.reload()
    }
  }

  const handleImportData = async () => {
    if (!parsedCsvData) return
    const processContext = spreadsheetData.processId
      ? {
          processId: spreadsheetData.processId,
          processSectionId: spreadsheetData.processSectionId!
        }
      : undefined

    setLoading(true)
    try {
      await performImport(processContext)
    } catch (e) {
      setLoading(false)
      setImportProgress(null)
      displayErrorMessage(e)
    }
  }

  // Build a lookup map for columns by publicId (avoids repeated .find() calls)
  const computeColumnMap = () => {
    const map = new Map<string, (typeof spreadsheetData.viewDetails.columns)[number]>()
    for (const col of spreadsheetData.viewDetails.columns) {
      map.set(col.publicId, col)
    }
    return map
  }
  const columnMap = computeColumnMap()

  // Pre-compute which columns are editable (not locked) for the current insertMethod/mergingColumn
  const computeEditableColumnIds = () => {
    const hiddenSet = new Set(spreadsheetData.userConfiguration.hiddenColumns)
    const editable = new Set<string>()
    for (const col of spreadsheetData.viewDetails.columns) {
      if (hiddenSet.has(col.publicId)) continue
      const isEditable =
        !col.isJoined &&
        !isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, col.publicId) &&
        ((insertMethod === 'upsert' && (col.scriptEnabled || col.formulaEnabled) && col.name === mergingColumn) ||
          (!col.scriptEnabled && !col.formulaEnabled))
      if (isEditable) editable.add(col.publicId)
    }
    return editable
  }
  const editableColumnIds = computeEditableColumnIds()

  const cleanRow = (row: Record<string, string>) => {
    const keys = Object.keys(row)
    const newRow: Record<string, unknown> = {}

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      // Skip internal properties used for filtering
      if (key === '__isBlankRow') continue
      const column = columnMap.get(key)
      if (!column || !editableColumnIds.has(key)) continue

      let value = row[key]
      if (!value) {
        // For upsert, include blank values as null so they overwrite existing data
        if (insertMethod === 'upsert') {
          newRow[column.name] = null
        }
        continue
      }

      // Track if we converted from Excel timestamp
      let wasExcelTimestamp = false

      // Convert Excel timestamps to ISO format (only for Excel files)
      if (isExcelFile && (column.kind === 'date' || column.kind === 'datetime') && isExcelTimestamp(value)) {
        const excelNum = parseFloat(value)
        if (column.kind === 'date') {
          value = convertExcelTimestampToISODate(excelNum)
        } else if (column.kind === 'datetime') {
          value = convertExcelTimestampToISODatetime(excelNum)
        }
        wasExcelTimestamp = true
      }

      // Apply the regular transformation
      if (column.kind === 'date' || column.kind === 'datetime') {
        // If we converted from Excel, the value is now in ISO format
        const inputDateFormat = wasExcelTimestamp ? 'YYYY-MM-DD' : dateFormat
        const inputDatetimeFormat = wasExcelTimestamp ? 'YYYY-MM-DD HH:mm:ss' : datetimeFormat
        newRow[column.name] = transformStringToCorrectFormat(value, column.kind, inputDateFormat, inputDatetimeFormat)
      } else {
        newRow[column.name] = transformStringToCorrectFormat(value, column.kind)
      }
    }
    return newRow
  }

  const computeTableData = () => {
    const data: Array<Record<string, ITableData>> = []
    if (!parsedCsvData) return data

    const hiddenSet = new Set(spreadsheetData.userConfiguration.hiddenColumns)
    const maxRows = showAllRows ? parsedCsvData.length : Math.min(parsedCsvData.length, 1000)

    for (let i = 0; i < maxRows; i++) {
      const dataObject: Record<string, ITableData> = {}
      const rowData = parsedCsvData[i]
      const rowKeys = Object.keys(rowData)

      for (let j = 0; j < rowKeys.length; j++) {
        const key = rowKeys[j]
        if (hiddenSet.has(key)) continue

        const column = columnMap.get(key)
        if (!column) continue

        let displayValue = rowData[key]

        // Convert Excel timestamps for date/datetime columns in preview (only for Excel files)
        if (isExcelFile && (column.kind === 'date' || column.kind === 'datetime') && isExcelTimestamp(displayValue)) {
          const excelNum = parseFloat(displayValue)
          const isoValue =
            column.kind === 'date'
              ? convertExcelTimestampToISODate(excelNum)
              : convertExcelTimestampToISODatetime(excelNum)

          displayValue = column.dateFormat
            ? column.kind === 'date'
              ? formatDate(isoValue, column.dateFormat)
              : formatDateTime(isoValue, column.dateFormat)
            : isoValue
        }

        dataObject[key] = {
          value: j,
          label: editableColumnIds.has(key) ? displayValue : ''
        }
      }
      data.push(dataObject)
    }
    return data
  }
  const tableData = computeTableData()

  // Whether we're still on the table selection step (no CSV loaded yet)
  const showTableSelection = fromTable && !file

  // ---------------------- Render ----------------------
  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      title={fromTable ? 'Import Data From Morta Table View' : 'Import Data From File (CSV / Excel)'}
      shouldBlockNavigation={loading}
      confirmMessage={
        insertMethod === 'upsert'
          ? 'There is an import in progress. Rows that have already been updated cannot be reverted. Are you sure you wish to continue?'
          : 'There is an import in progress. Cancelling will remove all imported rows. Are you sure you wish to continue?'
      }
      onClose={loading ? handleImportCancel : undefined}
    >
      {/* Step 1a: Table selection (when importing from table) */}
      {showTableSelection && (
        <ImportFromTableSelection onCsvReady={handleTableCsvReady} onCancel={() => setOpen(false)} />
      )}

      {/* Step 1b: File selection (when importing from file) */}
      {!fromTable && (!file || fileImportLoading) && (
        <div>
          <div className="mb-5">
            Please select the file you wish to upload — CSV (comma‑delimited) or Excel workbook (.xlsx / .xls).
          </div>
          <div className="mb-5">
            For Excel files, ensure your data is placed on the <span className="font-bold">first sheet</span>.
          </div>
          <Button
            onClick={(event) => {
              event.stopPropagation()
              inputRef.current!.click()
            }}
            className="w-[150px]"
            isLoading={fileImportLoading}
            disabled={noHeadersError}
          >
            Select File
          </Button>

          {file && file.size > LARGE_FILE_WARNING_SIZE && (
            <small className="text-red ml-2.5 mt-2.5">Uploading file, please wait.</small>
          )}

          {noHeadersError && (
            <small className="text-red ml-2.5 mt-2.5">
              There are no available columns to import into. Please enable at least one column.
            </small>
          )}
        </div>
      )}

      {/* Step 2: CSV options (shared by both flows) */}
      {file && !fileImportLoading && (
        <span className="italic mb-2.5">
          {fromTable ? `Importing from Morta Table${rowCountLabel}` : `${file.name}${rowCountLabel}`}
        </span>
      )}

      {fromTable && file && !parsedCsvData && (
        <div className="py-5 px-0">
          <Skeleton className="w-full block h-5" />
        </div>
      )}

      {file && parsedCsvData && (
        <div>
          {/* -- existing UI (insert/merge options, additional options etc.) stays unchanged -- */}
          <div className="mt-2.5 mb-2.5">
            <div className="mb-5">
              Would you like to append these rows to the end of the table or insert and merge? Inserting and merging
              means that an existing row will be updated if a specified value already exists in a table (using the merge
              column) and inserted as a new row if the specified value does not exist.
            </div>

            <Select
              options={[
                { label: 'Append', value: 'append' },
                { label: 'Insert and Merge', value: 'upsert' }
              ]}
              optionsSelected={insertMethod ? [insertMethod] : []}
              onOptionClick={(option) => setInsertMethod(option)}
            />
            <div className="mb-5" />
            {insertMethod === 'upsert' && (
              <Select
                options={spreadsheetData.viewDetails.columns.map((col) => ({ label: col.name, value: col.name }))}
                optionsSelected={mergingColumn ? [mergingColumn] : []}
                onOptionClick={(option) => setMergingColumn(option)}
              />
            )}
          </div>

          <Button
            className="mt-2.5 w-[200px]"
            internalType="outline"
            onClick={() => setShowAdditionalOptions(!showAdditionalOptions)}
          >
            {showAdditionalOptions ? 'Hide' : 'Show'} Additional Options
          </Button>

          {showAdditionalOptions && (
            <div className="text-sm flex flex-col bg-surface-sunken rounded gap-2.5 mt-2.5 py-2.5 px-5">
              <Checkbox checked={excludeFirstRow} onChange={(event) => setExcludeFirstRow(event.target.checked)}>
                Exclude First Row
              </Checkbox>
              <Checkbox checked={excludeBlankRows} onChange={(event) => setExcludeBlankRows(event.target.checked)}>
                Exclude Blank Rows
              </Checkbox>
              {!isExcelFile && (
                <div className="flex items-center">
                  <span className="mr-2 w-[200px]">Date Format:</span>
                  <Select
                    options={DATE_FORMAT_CANDIDATES.map((format) => ({
                      value: format,
                      label: format
                    }))}
                    optionsSelected={[dateFormat]}
                    onOptionClick={(option) => setDateFormat(option as AllowedDateFormats)}
                    searchable={false}
                  />
                </div>
              )}
              {!isExcelFile && (
                <div className="flex items-center">
                  <span className="mr-2 w-[200px]">Datetime Format:</span>
                  <Select
                    options={DATETIME_FORMAT_CANDIDATES.map((format) => ({
                      value: format,
                      label: format
                    }))}
                    optionsSelected={[datetimeFormat]}
                    onOptionClick={(option) => setDatetimeFormat(option as AllowedDatetimeFormats)}
                    searchable={false}
                  />
                </div>
              )}
            </div>
          )}

          <ErrorBoundary>
            {parsedCsvData.length > 1000 && (
              <div className="flex items-center justify-between mt-5 gap-2.5">
                <span className="text-sm italic">
                  {fileTruncated
                    ? 'Showing a preview of the first 1,000 rows. All rows will be imported.'
                    : !showAllRows
                      ? 'Displaying only top 1,000 rows in the preview, but all rows will be imported.'
                      : ''}
                </span>
                {!fileTruncated && (
                  <Button
                    internalType="outline"
                    onClick={() => setShowAllRows(!showAllRows)}
                    className="w-[150px]"
                    aria-label={showAllRows ? 'Collapse Rows' : 'Load All'}
                  >
                    {showAllRows ? 'Collapse Rows' : 'Load All'}
                  </Button>
                )}
              </div>
            )}
            <div className="mt-5 h-[400px]">
              <Table
                data={tableData}
                include={headers}
                sort={false}
                defaultSort={headers[0].id}
                defaultSortAscending={true}
              />
            </div>
          </ErrorBoundary>
          {importProgress && importProgress.total > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Importing rows... Please do not refresh or leave the page.</span>
                <span className="text-sm font-bold">
                  {Math.round((importProgress.current / importProgress.total) * 100)}%
                </span>
              </div>
              <ProgressBar
                percentage={(importProgress.current / importProgress.total) * 100}
                classNames={{
                  track: 'h-2 bg-surface-strong',
                  fill: 'h-2 bg-green transition-[width] duration-300 ease-in-out'
                }}
              />
            </div>
          )}
          <div className="flex mt-5">
            <Button
              className="ml-auto mr-2.5 w-[150px]"
              internalType="danger"
              onClick={async () => {
                if (loading) {
                  await handleImportCancel()
                }
                setOpen(false)
                setFileImportLoading(false)
              }}
              disabled={cancelling}
            >
              Cancel Import
            </Button>
            <Button
              internalType="accept"
              className="w-[150px]"
              onClick={handleImportData}
              isLoading={loading}
              disabled={(insertMethod === 'upsert' && !mergingColumn) || cancelling}
            >
              {cancelling ? 'Cancelling Import' : 'Import Data'}
            </Button>
          </div>
        </div>
      )}
      <input
        type="file"
        id="file"
        ref={inputRef}
        accept=".csv, .xlsx, .xls"
        onChange={handleFileChange}
        className="hidden"
      />
    </Modal>
  )
}

export default AppendFileModal
